import React, { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Plus, Maximize2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { CAPABILITIES, MARKET_STYLES, MARKETS } from '../../data/whatWeDo';

const FILTERS = [{ id: 'all', label: 'All' }, ...MARKETS.map((m) => ({ id: m.id, label: m.label }))];

function ConnectorLine({ beamBg, nodeBg }) {
  return (
    <div className="relative flex flex-col items-center">
      <div
        className={`pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 w-[480px] max-w-[80vw] h-44 ${beamBg} opacity-[0.12] blur-3xl rounded-full`}
        aria-hidden="true"
      />
      <motion.span
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        style={{ transformOrigin: 'top' }}
        transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
        className={`w-[3px] h-24 sm:h-32 lg:h-40 rounded-full ${beamBg}`}
      />
      <motion.span
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.52, duration: 0.3 }}
        className={`-mt-1.5 w-3 h-3 rotate-45 ${nodeBg}`}
      />
    </div>
  );
}

function MarketTag({ marketId }) {
  const s = MARKET_STYLES[marketId];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-[0.12em] ${s.tag}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function CapabilityTile({ cap, active, onClick }) {
  const Icon = cap.icon;
  const imgs = (cap.images || []).map((im) => (typeof im === 'string' ? { src: im } : im));
  const bg = imgs[0]?.src;
  return (
    <button
      onClick={onClick}
      data-testid={`capability-tile-${cap.key}`}
      aria-expanded={active}
      className={`group relative text-left rounded-xl border overflow-hidden h-full w-full min-h-[210px] transition-[border-color,box-shadow,transform] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest))] focus-visible:ring-offset-2 ${
        active
          ? 'border-[hsl(var(--forest))] shadow-elev-2 ring-4 ring-[hsl(var(--forest))] scale-[1.03] -translate-y-1 z-10'
          : 'border-border hover:-translate-y-1 hover:shadow-elev-2'
      }`}
    >
      {bg ? (
        <img
          src={bg}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
      ) : (
        <span className="absolute inset-0 bg-bone-2" />
      )}
      <span className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--coal))] via-[hsl(var(--coal)/0.55)] to-[hsl(var(--coal)/0.10)]" />
      {active && (
        <span className="absolute bottom-0 left-0 right-0 h-1.5 bg-[hsl(var(--forest))]" aria-hidden="true" />
      )}
      <div className="relative flex flex-col justify-between h-full p-5">
        <div className="flex items-start justify-between gap-3">
          <span className="w-11 h-11 rounded-lg bg-white/15 border border-white/30 backdrop-blur-sm flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
          </span>
          <span
            className={`w-9 h-9 rounded-full border-2 flex items-center justify-center shrink-0 shadow-md transition-[transform,background-color,border-color] duration-300 ${
              active
                ? 'rotate-45 bg-[hsl(var(--forest))] border-[hsl(var(--forest))]'
                : 'border-white bg-black/60 backdrop-blur-sm group-hover:bg-[hsl(var(--forest))] group-hover:border-[hsl(var(--forest))]'
            }`}
          >
            <Plus className="w-5 h-5 text-white" strokeWidth={3} />
          </span>
        </div>
        <h3 className="font-display font-semibold text-[17px] sm:text-lg text-white leading-snug mt-6 drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)]">
          {cap.title}
        </h3>
      </div>
    </button>
  );
}

function CapabilityDetail({ cap }) {
  const Icon = cap.icon;
  const imgs = (cap.images || []).map((im) => (typeof im === 'string' ? { src: im } : im));
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const open = lightboxIndex != null;
  const current = open ? imgs[lightboxIndex] : null;
  const multiple = imgs.length > 1;

  const showPrev = useCallback(
    () => setLightboxIndex((i) => (i == null ? i : (i - 1 + imgs.length) % imgs.length)),
    [imgs.length]
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i == null ? i : (i + 1) % imgs.length)),
    [imgs.length]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); showPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); showNext(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, showPrev, showNext]);

  // Show a one-time "swipe to browse" cue on mobile the first time the lightbox opens.
  useEffect(() => {
    if (!open || !multiple) { setShowHint(false); return undefined; }
    let isMobile = false;
    let seen = false;
    try {
      isMobile = window.matchMedia('(max-width: 639px)').matches;
      seen = localStorage.getItem('omc_lb_swipe_hint') === '1';
    } catch (e) { /* ignore */ }
    if (!isMobile || seen) return undefined;
    setShowHint(true);
    const t = setTimeout(() => {
      setShowHint(false);
      try { localStorage.setItem('omc_lb_swipe_hint', '1'); } catch (e) { /* ignore */ }
    }, 2600);
    return () => clearTimeout(t);
  }, [open, multiple]);

  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) showNext();
    else showPrev();
  };

  return (
    <>
      <div
        className="relative rounded-xl border border-[hsl(var(--forest)/0.4)] bg-background shadow-elev-2 overflow-hidden"
        data-testid="capability-detail"
      >
        <div className="absolute left-0 top-0 bottom-0 w-[4px] bg-[hsl(var(--forest))]" />
        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-6">
            <span className="w-12 h-12 rounded-xl bg-[hsl(var(--forest)/0.12)] border border-[hsl(var(--forest)/0.3)] flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6 text-[hsl(var(--forest))]" strokeWidth={1.6} />
            </span>
            <div className="min-w-0">
              <h3 className="font-display font-semibold text-xl sm:text-2xl text-foreground leading-tight">
                {cap.title}
              </h3>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {cap.markets.map((mid) => (
                  <MarketTag key={mid} marketId={mid} />
                ))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
            <div>
              <p className="text-base text-foreground/80 leading-[1.7] mb-6">{cap.desc}</p>
              <p className="font-display font-bold text-lg text-foreground mb-4">Scope of Work</p>
              <ul className="space-y-2.5">
                {cap.items.map((it) => (
                  <li key={it} className="text-[15px] sm:text-base text-foreground/80 leading-[1.55] flex items-start gap-3">
                    <span className="mt-[9px] w-2 h-2 rounded-[1px] rotate-45 shrink-0 bg-[hsl(var(--forest))]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              {imgs.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {imgs.map((im, idx) => (
                    <button
                      type="button"
                      key={im.src}
                      onClick={() => setLightboxIndex(idx)}
                      data-testid="capability-photo"
                      title={[im.location, im.caption].filter(Boolean).join(' — ')}
                      className="group relative aspect-square overflow-hidden rounded-md border border-foreground/10 bg-bone-2 block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest))]"
                    >
                      <img
                        src={im.src}
                        alt={im.caption || cap.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center">
                          <Maximize2 className="w-4 h-4 text-white" strokeWidth={2} />
                        </span>
                      </span>
                      {im.location && (
                        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pt-5 pb-1.5">
                          <span className="block truncate font-display font-medium text-white text-[11px] leading-tight">{im.location}</span>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setLightboxIndex(null); }}>
        <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="capability-lightbox">
          {current && (
            <>
              <DialogTitle className="sr-only">{current.location || cap.title}</DialogTitle>
              <div
                className="relative bg-black"
                style={{ touchAction: 'pan-y' }}
                onTouchStart={multiple ? onTouchStart : undefined}
                onTouchEnd={multiple ? onTouchEnd : undefined}
                data-testid="lightbox-swipe-area"
              >
                <img
                  src={current.src}
                  alt={current.caption || cap.title}
                  className="w-full max-h-[68vh] object-contain bg-black"
                />
                {multiple && (
                  <>
                    <button
                      type="button"
                      onClick={showPrev}
                      aria-label="Previous photo"
                      data-testid="lightbox-prev"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      onClick={showNext}
                      aria-label="Next photo"
                      data-testid="lightbox-next"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
                    </button>
                    <span
                      data-testid="lightbox-counter"
                      className="absolute bottom-3 right-3 rounded-full bg-black/60 backdrop-blur-sm px-2.5 py-1 font-mono text-[11px] tracking-[0.08em] text-white"
                    >
                      {lightboxIndex + 1} / {imgs.length}
                    </span>
                  </>
                )}
                {showHint && (
                  <div
                    className="sm:hidden pointer-events-none absolute inset-x-0 bottom-16 flex justify-center"
                    data-testid="lightbox-swipe-hint"
                  >
                    <motion.span
                      animate={{ x: [-7, 7, -7] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                      className="inline-flex items-center gap-2 rounded-full bg-black/75 backdrop-blur-sm px-4 py-2 text-white text-xs font-medium shadow-lg"
                    >
                      <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
                      Swipe to browse
                      <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
                    </motion.span>
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  {current.job && (
                    <span className="font-mono text-[11px] text-white bg-[hsl(var(--forest))] rounded px-2 py-0.5">Job #{current.job}</span>
                  )}
                  {current.location && (
                    <span className="font-display font-semibold text-foreground text-lg">{current.location}</span>
                  )}
                </div>
                <p className="text-sm text-foreground/70">{current.caption || cap.title}</p>
                {multiple && (
                  <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="lightbox-filmstrip">
                    {imgs.map((im, idx) => (
                      <button
                        key={im.src}
                        type="button"
                        onClick={() => setLightboxIndex(idx)}
                        data-testid={`lightbox-thumb-${idx}`}
                        aria-label={`View photo ${idx + 1}`}
                        aria-current={idx === lightboxIndex}
                        className={`relative shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-[border-color,opacity] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest))] ${
                          idx === lightboxIndex
                            ? 'border-[hsl(var(--forest))] opacity-100'
                            : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={im.src} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Step 2/3: capabilities as a 3x3 grid of compact tiles that open a detail
// panel below. The whole block stays hidden until a market pillar is chosen —
// selecting one stretches this section open (cascading tiles).
const Capabilities = forwardRef(function Capabilities({ activeMarket, onMarketChange }, ref) {
  const [openKey, setOpenKey] = useState(null);
  const [headerRef, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const detailRef = useRef(null);

  const revealed = activeMarket != null;
  const visible = activeMarket === 'all' ? CAPABILITIES : CAPABILITIES.filter((c) => c.markets.includes(activeMarket));
  const activeMarketObj = MARKETS.find((m) => m.id === activeMarket);
  const activeCap = visible.find((c) => c.key === openKey) || null;
  const accent = activeMarketObj ? MARKET_STYLES[activeMarket] : null;
  const beamBg = accent ? accent.rule : 'bg-[hsl(var(--forest))]';
  const nodeBg = accent ? accent.dot : 'bg-[hsl(var(--forest))]';

  const handleFilter = (id) => {
    onMarketChange(id);
    setOpenKey(null);
  };

  const handleTile = (key) => {
    const opening = openKey !== key;
    setOpenKey(opening ? key : null);
    if (opening) {
      setTimeout(() => {
        if (detailRef.current) detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 140);
    }
  };

  return (
    <section
      ref={ref}
      id="capabilities"
      className={`relative overflow-hidden ${
        revealed ? 'pt-8 pb-20 sm:pt-10 sm:pb-24 lg:pt-12 lg:pb-28 bg-bone-2' : 'py-0'
      }`}
      data-testid="capabilities-section"
    >
      <AnimatePresence initial={false}>
        {revealed && (
          <motion.div
            key="capabilities-reveal"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
            className="relative overflow-hidden"
            data-testid="capabilities-reveal"
          >
            {/* market-colored glow now travels with the ConnectorLine (under the selected pillar) */}
            <div className="absolute inset-0 bg-grain opacity-[0.03] pointer-events-none" />
            {/* connector: line descends from whichever pillar is selected (aligned to that column) */}
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-3">
              {activeMarket === 'all' ? (
                <div className="flex justify-center">
                  <ConnectorLine beamBg={beamBg} nodeBg={nodeBg} />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10" aria-hidden="true">
                  {MARKETS.map((m) => (
                    <div key={m.id} className="flex justify-center">
                      {activeMarket === m.id && (
                        <ConnectorLine key={activeMarket} beamBg={beamBg} nodeBg={nodeBg} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                ref={headerRef}
                initial={{ opacity: 0, y: 14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="max-w-3xl mb-9"
              >
                <p className="kicker mb-4" data-testid="capabilities-kicker">
                  Specialty Civil &amp; Process Mechanical Capabilities
                </p>
                <h2
                  className="editorial-headline text-4xl sm:text-5xl text-foreground mb-5 leading-[1.06]"
                  data-testid="capabilities-headline"
                >
                  The Technical Expertise Behind
                  <span className="italic font-bold text-forest"> Critical Infrastructure.</span>
                </h2>
                <p className="text-foreground/75 leading-[1.75] text-[15px] sm:text-base">
                  Whether supporting a municipal water system, industrial facility, or energy
                  project, Ozark Mountain Construction provides the specialty civil and process
                  mechanical expertise required to bring complex infrastructure into service.
                  Our crews perform the critical fieldwork that integrates structures, equipment,
                  utilities, piping, and process systems into fully operational facilities.
                </p>
              </motion.div>

              <div className="flex flex-wrap items-center gap-2 mb-7" data-testid="capabilities-filter">
                {FILTERS.map((f) => {
                  const isActive = activeMarket === f.id;
                  const s = f.id !== 'all' ? MARKET_STYLES[f.id] : null;
                  return (
                    <button
                      key={f.id}
                      onClick={() => handleFilter(f.id)}
                      data-testid={`capabilities-filter-${f.id}`}
                      className={`px-4 py-2 rounded-full text-[12.5px] font-medium border transition-colors duration-200 ${
                        isActive
                          ? s
                            ? s.filterActive
                            : 'bg-foreground text-background border-foreground'
                          : 'bg-background border-border text-foreground/70 hover:text-foreground hover:border-foreground/30'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>

              <AnimatePresence mode="wait">
                {activeMarketObj && (
                  <motion.div
                    key={activeMarketObj.id}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mb-8"
                    data-testid="capabilities-market-context"
                  >
                    <h3 className="font-display font-semibold text-foreground text-2xl sm:text-3xl leading-tight mb-2">
                      {activeMarketObj.title}
                    </h3>
                    <p className="text-base sm:text-lg text-foreground/65 leading-relaxed">
                      {activeMarketObj.bullets.join(' · ')}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div
                key={activeMarket}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="capabilities-grid"
              >
                {visible.map((cap, i) => (
                  <motion.div
                    key={cap.key}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.12 + i * 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                    className="h-full"
                  >
                    <CapabilityTile cap={cap} active={openKey === cap.key} onClick={() => handleTile(cap.key)} />
                  </motion.div>
                ))}
              </motion.div>

              <div ref={detailRef}>
                <AnimatePresence initial={false}>
                  {activeCap && (
                    <motion.div
                      key={activeCap.key}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4">
                        <CapabilityDetail cap={activeCap} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
});

export default Capabilities;
